import StaffingSearchViewModel from '../StaffingTable/StaffingSearchViewModel'
import {
  FetchUserWorkspaceMemberships,
  GetCurrentUserWorkspaceMemberships,
} from 'iam/WorkspaceMembershipStore'
import { FilterStorageFactory } from 'infra/FiltersViewModel'
import { GetCurrentMember } from 'iam/SessionStore'
import { AlertFactory } from 'alerts/Alert'
import { action, computed, makeObservable, observable } from 'mobx'
import {
  StaffingColumnDetails,
  StaffingTableColumns,
} from '../StaffingTable/StaffingTableColumns'
import { TableSortOrder } from 'components/Table/ClickableTable'
import WorkspacePosting from '../../WorkspacePosting'
import WorkspacePostingStore from 'admin/WorkspacePostingStore'
import StaffingDetailsViewModel from '../StaffingDetailsPanel/StaffingDetailsViewModel'
import { task } from 'mobx-task'
import CreateWorkspacePostingViewModel from '../CreateWorkspacePostingDialog/CreateWorkspacePostingViewModel'
import UpdateWorkspacePostingViewModel from '../UpdateWorkspacePostingDialog/UpdateWorkspacePostingViewModel'

export default class StaffingViewModel {
  searcher = new StaffingSearchViewModel(
    this.workspacePostingStore,
    this.fetchWorkspaceMemberships,
    this.getWorkspaceMemberships,
    this.createFilterStorage('WorkspacePostings'),
  )

  /**
   * View model for the staffing details panel.
   */
  staffingDetailsVM = new StaffingDetailsViewModel(
    this.workspacePostingStore,
    (workspacePosting: WorkspacePosting) =>
      this.openUpdateWorkspacePostingDialog(workspacePosting),
    this.closeNav,
    this.navVisible,
    this.newAlert,
    this.refreshStatusFilter.bind(this),
  )

  /**
   * View model for creating a workspace posting.
   */
  createWorkspacePostingVM = new CreateWorkspacePostingViewModel(
    this.workspacePostingStore,
    this.newAlert,
    () => this.refreshStatusFilter(),
    this.getWorkspaceMemberships,
  )

  /**
   * View model for updating a workspace posting.
   */
  updateWorkspacePostingVM = new UpdateWorkspacePostingViewModel(
    this.workspacePostingStore,
    this.newAlert,
    () => this.refreshStatusFilter(),
    this.getWorkspaceMemberships,
  )

  /**
   * Columns to populate the staffing table header and rows with.
   */
  columns = StaffingTableColumns

  /**
   * Ensure we only activate the searcher once, as we want to keep it around
   * for subsequent page views of workspace postings.
   */
  private activateSearcher = () => this.searcher.activate()

  constructor(
    private workspacePostingStore: WorkspacePostingStore,
    private fetchWorkspaceMemberships: FetchUserWorkspaceMemberships,
    private getWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private createFilterStorage: FilterStorageFactory,
    private getCurrentMember: GetCurrentMember,
    private newAlert: AlertFactory,
    private closeNav: () => void,
    private navVisible: boolean,
  ) {
    makeObservable(this, {
      columns: observable,
      filteredItems: computed,
      refreshStatusFilter: action.bound,
      openUpdateWorkspacePostingDialog: action.bound,
      selectWorkspacePosting: action.bound,
    })
  }

  activate = task.resolved(async () => {
    await this.activateSearcher()
  })

  deactivate() {
    this.searcher.deactivate()
  }

  /**
   * Filtered workspace postings.
   */
  get filteredItems() {
    return this.searcher.filteredItems
  }

  /**
   * Gets the sort order for the specified column.
   *
   * @param column
   * @returns
   */
  getSortOrder(column: StaffingColumnDetails): TableSortOrder {
    return this.searcher.sorting.field === column.field
      ? this.searcher.sorting.direction
      : 'none'
  }

  /**
   * Sets the sort order for the specified column.
   *
   * @param column
   * @param direction
   * @returns
   */
  setSortOrder(column: StaffingColumnDetails, direction: TableSortOrder) {
    if (!column.field) {
      return
    }

    this.searcher.setSortField(column.field)
    this.searcher.setSortDirection(direction)
  }

  /**
   * Refreshes status filter.
   */
  async refreshStatusFilter() {
    await this.searcher.refreshStatusFilter()
  }

  /**
   * Opens the update workspace posting dialog.
   * @param workspacePosting
   */
  openUpdateWorkspacePostingDialog(workspacePosting: WorkspacePosting) {
    this.updateWorkspacePostingVM.showWithData(workspacePosting)
  }

  /**
   * Shows a posting in the details panel.
   * @param workspacePosting
   */
  selectWorkspacePosting(workspacePosting: WorkspacePosting) {
    this.staffingDetailsVM.show(workspacePosting)
  }
}
